import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'sonner'
import { Loader2, Save } from 'lucide-react'
import { z } from 'zod'
import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Form, FormControl, FormItem, FormMessage } from '@/components/ui/form'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { ImageUploadV2 } from '@/components/shared/inputComponents/ImageUploadV2'
import { cn } from '@/lib/utils'
import {
  GET_PRICELIST_SETTINGS,
  UPDATE_PRICELIST_SETTINGS,
} from './pricelistSettingsQueries'

const pricelistSettingsFormSchema = z.object({
  pricelist_export_enabled: z.boolean(),
  pricelist_cover_enabled: z.boolean(),
  pricelist_cover_imageId: z.number().nullable(),
  pricelist_cover_terms: z.string().max(200, 'Maximum 200 characters'),
})

type PricelistSettingsFormValues = z.infer<typeof pricelistSettingsFormSchema>

export default function PricelistSettings() {
  const { data, loading, error } = useQuery(GET_PRICELIST_SETTINGS)
  const [updatePricelistSettings, { loading: saving }] = useMutation(
    UPDATE_PRICELIST_SETTINGS,
    {
      onError: () => toast.error('Failed to save pricelist settings'),
    }
  )

  const form = useForm<PricelistSettingsFormValues>({
    resolver: zodResolver(pricelistSettingsFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      pricelist_export_enabled: false,
      pricelist_cover_enabled: false,
      pricelist_cover_imageId: null,
      pricelist_cover_terms: '',
    },
  })

  const shopSetting = data?.findFirstShopSetting
  // No `id` to save against => the form must not look editable/saveable.
  const loadFailed = !loading && !shopSetting
  const coverEnabled = form.watch('pricelist_cover_enabled')

  useEffect(() => {
    if (loading) return

    form.reset({
      pricelist_export_enabled: shopSetting?.pricelist_export_enabled ?? false,
      pricelist_cover_enabled: shopSetting?.pricelist_cover_enabled ?? false,
      pricelist_cover_imageId: shopSetting?.pricelist_cover_imageId ?? null,
      pricelist_cover_terms: shopSetting?.pricelist_cover_terms ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data])

  const onSubmit = async (values: PricelistSettingsFormValues) => {
    if (!shopSetting) {
      toast.error(
        'Pricelist settings could not be loaded, so there is nothing to save. Reload the page and try again.'
      )
      return
    }

    try {
      // `shopSettingsId` must always be sent (resolver no-ops without it);
      // a null `pricelist_cover_imageId` clears the image.
      await updatePricelistSettings({
        variables: {
          shopSettingsId: shopSetting.id,
          pricelist_export_enabled: values.pricelist_export_enabled,
          pricelist_cover_enabled: values.pricelist_cover_enabled,
          pricelist_cover_imageId: values.pricelist_cover_imageId,
          pricelist_cover_terms: values.pricelist_cover_terms.trim() || null,
        },
      })
      toast.success('Pricelist settings saved')
    } catch (saveError) {
      console.error('Save pricelist settings error:', saveError)
    }
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex justify-between items-center mb-8">
            <HeaderSection
              title="Pricelist"
              subtitle="Configure the B2B pricelist export and its cover sheet"
            />
            <Button type="submit" disabled={saving || loadFailed}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>

          {loadFailed && (
            <div className="max-w-[1000px] mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error
                ? `Failed to load pricelist settings: ${error.message}`
                : 'Pricelist settings are unavailable right now, so changes here cannot be saved.'}
            </div>
          )}

          <div className="max-w-[1000px] space-y-6">
            <div
              className={cn(loadFailed && 'pointer-events-none opacity-60')}
              aria-disabled={loadFailed}
            >
              <Card className="p-6">
                <InputFieldContainer
                  label="Enable pricelist export"
                  description="When off, B2B customers can't see or download the pricelist."
                  control={form.control}
                  name="pricelist_export_enabled"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Switch
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Card>

              <Card className="p-6 mt-6 space-y-6">
                <InputFieldContainer
                  label="Include cover sheet"
                  description="Adds a cover page to the exported pricelist."
                  control={form.control}
                  name="pricelist_cover_enabled"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Switch
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Dimmed (not disabled) when the cover is off so the stored
                    values stay visible and are still saved. */}
                <div
                  className={cn(
                    'space-y-6',
                    !coverEnabled && 'opacity-50'
                  )}
                >
                  <InputFieldContainer
                    label="Cover image"
                    description="One image containing the PRICELIST title and hero photo, portrait-friendly. It is placed on an A4 cover page; the date, terms and brand logo are added automatically."
                    control={form.control}
                    name="pricelist_cover_imageId"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <ImageUploadV2
                            field={field}
                            imageName="pricelist-cover"
                            existingImageUrl={
                              shopSetting?.pricelist_cover_image?.src
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Cover terms text"
                    description="Shown on the cover page, e.g. delivery terms."
                    control={form.control}
                    name="pricelist_cover_terms"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="EXW, IT"
                            maxLength={200}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Card>
            </div>
          </div>
        </form>
      </Form>
    </PageContent>
  )
}
